import * as THREE from 'three'
import type { TopologyNode, TopologyEdge } from '../stores/realtimeStore'

const FLOOR_HEIGHT = 3.0

function rssiColor(rssi: number): THREE.Color {
  // -30 dBm = excellent (green), -90 dBm = poor (red)
  const t = Math.max(0, Math.min(1, (rssi + 90) / 60))
  return new THREE.Color(1 - t, t, 0)
}

export class TopologyRenderer {
  group: THREE.Group
  private lines: THREE.LineSegments | null = null

  constructor() {
    this.group = new THREE.Group()
    this.group.name = 'topology'
  }

  setTopology(nodes: TopologyNode[], edges: TopologyEdge[]) {
    this.clear()
    if (nodes.length === 0 || edges.length === 0) return

    const nodeMap = new Map<string, TopologyNode>()
    for (const n of nodes) {
      nodeMap.set(n.id, n)
    }

    const positions: number[] = []
    const colors: number[] = []

    for (const edge of edges) {
      const from = nodeMap.get(edge.from)
      const to = nodeMap.get(edge.to)
      if (!from || !to) continue

      const y1 = from.floor * FLOOR_HEIGHT + 0.5
      const y2 = to.floor * FLOOR_HEIGHT + 0.5
      positions.push(from.x, y1, from.z, to.x, y2, to.z)

      const color = rssiColor(edge.rssi)
      colors.push(color.r, color.g, color.b, color.r, color.g, color.b)
    }

    if (positions.length === 0) return

    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3))

    const material = new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.6,
      linewidth: 1,
    })

    this.lines = new THREE.LineSegments(geometry, material)
    this.group.add(this.lines)
  }

  setVisible(visible: boolean) {
    this.group.visible = visible
  }

  clear() {
    if (this.lines) {
      this.group.remove(this.lines)
      this.lines.geometry.dispose()
      ;(this.lines.material as THREE.Material).dispose()
      this.lines = null
    }
  }

  dispose() {
    this.clear()
  }
}
